<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { type WithAttributes, type Labeled, toHtml, fw, ui } from './attributes'

import { useContext } from './context'

import Label from '~/ui/Label.vue'
import Button from '~/ui/Button.vue'

defineOptions({
  /* No attrs for label */
  inheritAttrs: false
})

// TODO: Tighten types and expand taxonomy: `['password' | 'search' | 'username' | 'numeric' | 'email']?: true`
const {
  uiBlockSize = useContext().value.uiBlockSize ?? '48',
  uiSunken = useContext().value.uiSunken ?? undefined,
  ...props
} = defineProps<WithAttributes<{
  class: {
    color?: 'color-destructive-solid' | 'color-default-solid'
    shape?: 'shape-circle' | 'shape-field' | 'shape-square' | 'shape-field-grow' | 'shape-field-minimal' | 'shape-field-full'
  }
  uiBlockSize?: '24' | '40' | '48'
  uiSunken?: boolean
  uiInlineSize?: keyof typeof ui['inlineSize']

  placeholder?: string
  password?: true
  search?: true
  numeric?: true
  autofocus?: boolean
  reset?: () => void
  label?: string
  required?: boolean
  disabled?: boolean
} & Labeled>>()

// TODO(A11y): Add `inputmode="numeric" pattern="[0-9]*"` to input if model type is number:
// https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
// const isNumeric = restProps.numeric

const isShowingPassword = ref(false)
const id = useId()

// TODO: Accept fallback $attrs:  `const fallthroughAttrs = useAttrs()`

// TODO: Implement `copy password` button?

const type = computed(() =>
  props.password && !isShowingPassword.value ? 'password'
    : props.search ? 'search'
      : props.numeric ? 'numeric'
        : undefined
)

const { t } = useI18n()

const input = ref()

// TODO: Use declarative `auofocus` with v-focus [WIP] instead of imperative focus handling
defineExpose({
  focus: () => input.value.focus()
})

const previouslyFocusedElement = ref()

onMounted(() => props.autofocus && nextTick(() => {
  previouslyFocusedElement.value = document.activeElement
  previouslyFocusedElement.value?.blur()
  input.value.focus()
}))

onUnmounted(() =>
  previouslyFocusedElement.value?.focus()
)

// #region model
const model = defineModel<string | number>({ required: true })
// #endregion model

useContext({ uiBlockSize: '40' })
</script>

<template>
  <div :class="$style.inputContainer">
    <div
      :class="[
        fw.layout.row,
        !props.class?.includes('color') && fw.color.defaultSolid,
        !props.class?.includes('shape') && fw.shape.field,
        props.class,
        $style.input
      ]"
      :ui-block-size
      :ui-inline-size="uiInlineSize ?? '288'"
      :ui-sunken
      ui-gap="0"
    >
      <div
        v-if="$slots.start || props.search"
        :class="$style.start"
      >
        <slot name="start" />
        <Icon
          v-if="props.search"
          icon="bi:search"
        />
      </div>
      <Label
        :label
        :required
      >
        <template
          v-if="$slots.label"
          #label
        >
          <slot name="label" />
        </template>
        <input
          v-bind="{ ...$attrs, ...toHtml<'input'>({ ...$attrs, ...props }) }"
          ref="input"
          v-model="model"
          size="1"
          :type
          :class="fw.font.caption"
          :ui-block-size
          ui-indicate-overflow
          :placeholder
          @click.stop
          @blur="isShowingPassword = false"
          @keyup.escape="isShowingPassword = false"
        >
      </Label>

      <div
        v-if="$slots.end || props.search || props.password || props.reset"
        :class="[$style.end, fw.layout.row]"
        :ui-gap="ui.gap[4]"
      >
        <Button
          v-if="props.password"
          shows-tooltip
          class="color-default-solid shape-square"
          role="switch"
          ui-block-size="48"
          :aria-checked="isShowingPassword"
          :aria-labelledby="id"
          @click="isShowingPassword = !isShowingPassword"
          @blur="(e:MouseEvent) => {
            // Hide password if the related (new) target is not the input
            isShowingPassword
              = e.relatedTarget && 'value' in e.relatedTarget && e.relatedTarget.value === model
                ? isShowingPassword
                : false
          }"
        >
          <Icon :icon="!isShowingPassword ? 'bi:eye' : 'bi:eye-slash-fill'" />
        </Button>
        <Button
          v-if="props.search"
          class="color-primary-solid"
          ui-block-size="48"
        >
          {{ t('components.Sidebar.link.search') }}
        </Button>
        <Button
          v-if="props.reset"
          class="color-primary-ghost shape-square"
          shows-tooltip
          :aria-labelledby="id"
          @click="reset"
        >
          <Icon icon="bi:arrow-counterclockwise" />

          <Icon icon="bi:arrow-counterclockwise" />
        </Button>
        <slot name="end" />
      </div>

      <!-- Floating label for icon-buttons -->
      <label
        :id
        :class="$style.floating"
        role="tooltip"
      >
        {{ props.reset
          ? t('components.library.EditForm.button.reset')
          : props.password ? (
            isShowingPassword ? t('vui.aria.password.hide') : t('vui.aria.password.show')
          ) : '' }}
      </label>
    </div>
  </div>
</template>

<style module>
.inputContainer {
  /*
   * Invisible pass-through everywhere by default, so nothing about Input's layout
   * changes for existing (non-table) callers. `Table.vue`'s `.table > *` cell rule is
   * more specific and overrides this to `display: flex` for its own direct children,
   * turning this into a real box that Table can force to a uniform row height and
   * vertically center — instead of that height/centering landing directly on the
   * visual field box itself (which has no room to be its own natural, smaller height).
   */
  display: contents;
}

.input {
    position: relative;
    color: var(--color-foreground-800);
    padding: 0;

    transition: .3s box-shadow;
    &:has(input:hover), &:focus {
      box-shadow: inset 0 0 0 4px var(--color-background-300);
    }

    &::placeholder {
      color: var(--color-foreground-600);
    }

    /* Optional label wrapper (excluding the floating tooltip) extends to fill available space */
    > label:not(.floating) {
      flex: 1 1 0;
      min-inline-size: 0; /* Firefox fix*/
    }
  input {
    flex: 1 1 0;  /* Grow to fill available space, shrink when parent is smaller, start at 0 */
    min-inline-size: 0;   /* Firefox fix */

    width: 100%;

    cursor: text;
    border: none;
    margin: 0;
    padding-inline-start: 16px;
    --mask-padding-inline-start: 16px;
    padding-inline-end: 0px;
    animation: none; /* the animation is designed for scrolling containers */
    --mask-end: 0;

    &:last-child {
        padding-inline-end: 16px;
        --mask-padding-inline-end: 16px;

    }

    &:focus-visible {
        outline: none;
        --mask-end: 1;
    }

  }

  button {
      outline-offset: -4px;
  }

  &:has(:focus-visible) {
      box-shadow: inset 0 0 0 4px var(--color-focus);
  }

  > .start:not(:empty) {
      inline-size: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
  }
  &:has(.start:not(:empty)) input {
    padding-inline-start: 44px;
  }

  &:has([shows-tooltip]:is(:hover, :focus-visible)) label.floating:not(:empty) {
    opacity: 1;
  }

  label.floating {
    position: absolute;
    opacity: 0;
    transition: opacity .2s;
    right: 8px;
    bottom: 37px;
    font-size: 12px;
    background: var(--color-900);
    color: var(--color-200);
    padding: 0 8px;
    outline: .5px solid currentcolor;
    pointer-events: none;
  }
}
</style>
